<template>
  <div class="view-selector-container" ref="selectorContainer">
    <button class="view-selector-button" @click.prevent="toggleMenu" ref="selectorButton" :title="currentViewLabel">
      <ViewListIcon :size="18" />
      <span class="view-label">{{ currentViewLabel }}</span>
      <ChevronDownIcon :size="16" class="chevron-icon" :class="{ 'rotated': isMenuOpen }" />
    </button>
    <Teleport to="body">
      <Transition name="menu-slide" mode="out-in">
        <div v-if="isMenuOpen" class="menu-wrapper" :style="menuStyle">
          <ViewSelectorMenu :selected-view-id="modelValue" :view-types="viewTypes" @select-view="handleSelectView" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import ViewListIcon from 'vue-material-design-icons/ViewList.vue';
import ChevronDownIcon from 'vue-material-design-icons/ChevronDown.vue';
import ViewSelectorMenu from '../menus/ViewSelectorMenu.vue';

export default {
  name: 'ViewSelector',
  props: {
    modelValue: {
      type: String,
      required: true
    },
    viewTypes: {
      type: Array,
      required: true,
      validator: (value) => {
        return Array.isArray(value) && value.every(view =>
          view && typeof view.id === 'string' && typeof view.label === 'string'
        );
      }
    }
  },
  data() {
    return {
      isMenuOpen: false,
      menuPosition: { top: 0, left: 0 }
    };
  },
  computed: {
    currentViewLabel() {
      const currentView = this.viewTypes.find(view => view.id === this.modelValue);
      return currentView?.label || 'Select View';
    },
    menuStyle() {
      return {
        top: `${this.menuPosition.top}px`,
        left: `${this.menuPosition.left}px`
      };
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleMenu() {
      if (!this.isMenuOpen) {
        const button = this.$refs.selectorButton;
        if (button) {
          const rect = button.getBoundingClientRect();
          this.menuPosition = {
            top: rect.bottom + 8,
            left: rect.left
          };
        }
      }
      this.isMenuOpen = !this.isMenuOpen;
    },
    handleClickOutside(event) {
      const selectorContainer = this.$refs.selectorContainer;
      const clickedSelector = selectorContainer && selectorContainer.contains(event.target);
      const clickedMenu = event.target.closest('.menu-wrapper');

      if (!clickedSelector && !clickedMenu) {
        this.isMenuOpen = false;
      }
    },
    handleSelectView(viewId) {
      this.$emit('update:modelValue', viewId);
      this.isMenuOpen = false;
    }
  },
  emits: ['update:modelValue'],
  components: { ViewListIcon, ChevronDownIcon, ViewSelectorMenu }
}
</script>

<style scoped>
.view-selector-container {
  position: relative;
}

.view-selector-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 14px;
  color: #333;
  height: 32px;
  box-sizing: border-box;
  white-space: nowrap;
}

.view-selector-button:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.view-selector-button:active {
  background-color: #ebebeb;
}

.view-label {
  font-weight: 500;
  user-select: none;
}

.chevron-icon {
  transition: transform 0.2s ease;
  color: #666;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

/* Menu wrapper */
.menu-wrapper {
  position: fixed;
  z-index: 1000;
}

/* Menu expand animation - from top left */
.menu-slide-enter-active {
  transition: all 0.2s ease-out;
}

.menu-slide-leave-active {
  transition: all 0.15s ease-in;
}

.menu-slide-enter-from {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top left;
}

.menu-slide-leave-to {
  opacity: 0;
  transform: scale(0.95);
  transform-origin: top left;
}
</style>
